import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Alert,
  CircularProgress,
} from '@mui/material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { pl } from 'date-fns/locale';
import { useTranslation } from 'react-i18next';
import { Timeoff, Hairdresser } from '../types/entities';
import { TimeoffFormData } from '../types/forms';

interface TimeoffFormModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: TimeoffFormData) => Promise<void>;
  title: string;
  submitButtonText: string;
  hairdressers: Hairdresser[];
  initialData?: TimeoffFormData;
  isEdit?: boolean;
}

const TimeoffFormModal: React.FC<TimeoffFormModalProps> = ({
  open,
  onClose,
  onSubmit,
  title,
  submitButtonText,
  hairdressers,
  initialData,
  isEdit = false,
}) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState<TimeoffFormData>({
    hairdresserId: 0,
    startDate: '',
    endDate: '',
    description: '',
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (open) {
      if (initialData) {
        setFormData(initialData);
      } else {
        setFormData({
          hairdresserId: hairdressers.length > 0 ? hairdressers[0].id : 0,
          startDate: '',
          endDate: '',
          description: '',
        });
      }
      setErrors({});
    }
  }, [open, initialData, hairdressers]);

  const handleInputChange = (field: keyof TimeoffFormData, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: '',
      }));
    }
  };


  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.hairdresserId || formData.hairdresserId === 0) {
      newErrors.hairdresserId = t('forms.required');
    }

    if (!formData.startDate) {
      newErrors.startDate = t('forms.required');
    }

    if (!formData.endDate) {
      newErrors.endDate = t('forms.required');
    }

    if (formData.startDate && formData.endDate) {
      const startDate = new Date(formData.startDate);
      const endDate = new Date(formData.endDate);
      
      if (endDate < startDate) {
        newErrors.endDate = t('timeoffs.toDateError');
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    // Additional check to prevent submission with invalid hairdresser ID
    if (!formData.hairdresserId || formData.hairdresserId === 0) {
      setErrors({ hairdresserId: t('forms.required') });
      return;
    }

    setLoading(true);
    try {
      await onSubmit(formData);
      onClose();
    } catch (error) {
      console.error('Error submitting form:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      onClose();
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={pl}>
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <form onSubmit={handleSubmit}>
          <DialogTitle>{title}</DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
              <FormControl fullWidth error={!!errors.hairdresserId}>
                <InputLabel>{t('common.hairdresser')}</InputLabel>
                <Select
                  value={formData.hairdresserId}
                  onChange={(e) => handleInputChange('hairdresserId', Number(e.target.value))}
                  label={t('common.hairdresser')}
                >
                  {hairdressers.map((hairdresser) => (
                    <MenuItem key={hairdresser.id} value={hairdresser.id}>
                      {hairdresser.name}
                    </MenuItem>
                  ))}
                </Select>
                {errors.hairdresserId && (
                  <Alert severity="error" sx={{ mt: 1 }}>
                    {errors.hairdresserId}
                  </Alert>
                )}
              </FormControl>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <DateTimePicker
                  label={t('timeoffs.startDate')}
                  value={formData.startDate ? new Date(formData.startDate) : null}
                  onChange={(date: Date | null) => handleInputChange('startDate', date ? date.toISOString() : '')}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      error: !!errors.startDate,
                      helperText: errors.startDate,
                    },
                  }}
                />
                <DateTimePicker
                  label={t('timeoffs.endDate')}
                  value={formData.endDate ? new Date(formData.endDate) : null}
                  onChange={(date: Date | null) => handleInputChange('endDate', date ? date.toISOString() : '')}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      error: !!errors.endDate,
                      helperText: errors.endDate,
                    },
                  }}
                />
              </Box>

              <TextField
                fullWidth
                label={t('common.description')}
                value={formData.description || ''}
                onChange={(e) => handleInputChange('description', e.target.value)}
                multiline
                rows={3}
                placeholder={t('timeoffs.timeoffDescription')}
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose} disabled={loading}>
              {t('common.cancel')}
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={loading}
              startIcon={loading ? <CircularProgress size={20} /> : null}
            >
              {loading ? t('common.loading') : submitButtonText}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </LocalizationProvider>
  );
};

export default TimeoffFormModal;
